import { useState } from 'react';
import { useQuery } from 'react-query';
import { Plus, Trash2, X } from 'lucide-react';
import { trekAPI } from '../../services/api';
import { parseFooterCategories, serializeFooterCategories } from '../../utils/siteLabelsEditors';
import {
  footerCategoryItemHasTreks,
  getFooterItemTrekIds,
  getFooterItemTrekSlugs,
  normalizeFooterCategoryItem
} from '../../utils/footerCategoryItem.js';

function itemKey(item) {
  const ids = getFooterItemTrekIds(item).join(',');
  const slugs = getFooterItemTrekSlugs(item).join(',');
  return `${item?.label}:${ids}:${slugs}`;
}

function trekRefsFromIds(trekOptions, ids) {
  return ids
    .map((id) => trekOptions.find((t) => t.id === id))
    .filter(Boolean);
}

function setItemTreks(item, trekOptions, selectedIds) {
  const treks = trekRefsFromIds(trekOptions, selectedIds);
  const next = { ...item };
  delete next.trekId;
  delete next.slug;
  if (treks.length) {
    next.trekIds = treks.map((t) => t.id);
    next.trekSlugs = treks.map((t) => t.slug).filter(Boolean);
  } else {
    delete next.trekIds;
    delete next.trekSlugs;
  }
  return normalizeFooterCategoryItem(next);
}

/**
 * @param {{ value: string, onChange: (json: string) => void }} props
 */
export default function FooterCategoriesEditor({ value, onChange }) {
  const groups = parseFooterCategories(value);
  const [rowTrekPickerOpen, setRowTrekPickerOpen] = useState({});
  const [rowTrekSearch, setRowTrekSearch] = useState({});

  const { data: trekOptions = [], isLoading: treksLoading } = useQuery(
    ['site-labels', 'footer-treks-all'],
    async () => {
      const res = await trekAPI.list({ page: 1, limit: 500, isActive: true });
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      return rows
        .map((t) => ({
          id: String(t.id),
          name: String(t.name || '').trim(),
          slug: String(t.slug || '').trim()
        }))
        .filter((t) => t.name && t.slug)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    { staleTime: 60_000 }
  );

  const update = (next) => {
    onChange(serializeFooterCategories(next, { forEditor: true }));
  };

  const rowSearchKey = (gIndex, iIndex) => `${gIndex}-${iIndex}`;

  const filteredTreks = (searchTerm) => {
    const q = String(searchTerm || '')
      .trim()
      .toLowerCase();
    if (!q) return trekOptions;
    return trekOptions.filter(
      (t) => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
    );
  };

  const toggleRowTrek = (gIndex, iIndex, item, trekId) => {
    const current = new Set(getFooterItemTrekIds(item));
    if (current.has(trekId)) current.delete(trekId);
    else current.add(trekId);
    const nextItem = setItemTreks(item, trekOptions, [...current]);
    setItemAt(gIndex, iIndex, nextItem);
  };

  const removeRowTrek = (gIndex, iIndex, item, trekId) => {
    const current = getFooterItemTrekIds(item).filter((id) => id !== trekId);
    setItemAt(gIndex, iIndex, setItemTreks(item, trekOptions, current));
  };

  const setItemAt = (gIndex, iIndex, patchOrItem) => {
    const next = groups.map((g, gi) => {
      if (gi !== gIndex) return g;
      const items = g.items.map((it, ii) => {
        if (ii !== iIndex) return it;
        const merged =
          typeof patchOrItem === 'object' && patchOrItem !== null && !Array.isArray(patchOrItem)
            ? normalizeFooterCategoryItem({ ...it, ...patchOrItem })
            : normalizeFooterCategoryItem(patchOrItem);
        return merged;
      });
      return { ...g, items };
    });
    update(next);
  };

  const addLinkRow = (gIndex, withTrekPickerOpen = false) => {
    const next = groups.map((g, gi) =>
      gi === gIndex ? { ...g, items: [...(g.items || []), { label: '', search: '' }] } : g
    );
    update(next);
    if (withTrekPickerOpen) {
      const iIndex = (groups[gIndex]?.items?.length || 0);
      setRowTrekPickerOpen((prev) => ({ ...prev, [rowSearchKey(gIndex, iIndex)]: true }));
    }
  };

  return (
    <div className="site-labels-visual">
      <p className="form-hint site-labels-visual-intro">
        Set the <strong>link label</strong> (footer text visitors see). Optionally link one or more{' '}
        <strong>treks</strong> — clicking goes to those events (one trek → event page; several → filtered
        events list). With no treks, use <strong>search text</strong> for the Events page search instead.
      </p>
      {groups.map((group, gIndex) => (
        <div className="site-labels-visual-group card" key={`fg-${gIndex}`}>
          <div className="site-labels-visual-group-head">
            <label className="site-labels-visual-field site-labels-visual-field-grow">
              <span className="site-labels-visual-label">Column title</span>
              <input
                type="text"
                className="input"
                value={group.heading}
                placeholder="e.g. Trending"
                onChange={(e) => {
                  update(
                    groups.map((g, i) => (i === gIndex ? { ...g, heading: e.target.value } : g))
                  );
                }}
              />
            </label>
            <button
              type="button"
              className="btn btn-ghost site-labels-visual-remove"
              aria-label="Remove column"
              onClick={() => update(groups.filter((_, i) => i !== gIndex))}
              disabled={groups.length <= 1}
            >
              <Trash2 size={18} />
            </button>
          </div>

          <div className="site-labels-visual-subrows">
            {(group.items || []).map((item, iIndex) => {
              const rk = rowSearchKey(gIndex, iIndex);
              const selectedIds = getFooterItemTrekIds(item);
              const selectedTreks = trekRefsFromIds(trekOptions, selectedIds);
              const hasTreks = footerCategoryItemHasTreks(item);
              const pickerOpen = rowTrekPickerOpen[rk];

              return (
                <div
                  className="site-labels-visual-row site-labels-visual-row-nested footer-category-link-row footer-category-link-row-stack"
                  key={`fi-${gIndex}-${iIndex}-${itemKey(item)}`}
                >
                  <div className="footer-category-link-row-main">
                    <label className="site-labels-visual-field site-labels-visual-field-grow">
                      <span className="site-labels-visual-label">Link label (shown in footer)</span>
                      <input
                        type="text"
                        className="input"
                        value={item.label}
                        placeholder="e.g. High Altitude Trek"
                        onChange={(e) => setItemAt(gIndex, iIndex, { label: e.target.value })}
                      />
                    </label>
                    {!hasTreks ? (
                      <label className="site-labels-visual-field site-labels-visual-field-grow">
                        <span className="site-labels-visual-label">Events search (no trek linked)</span>
                        <input
                          type="text"
                          className="input"
                          value={item.search}
                          placeholder="Filter text on /events"
                          onChange={(e) => setItemAt(gIndex, iIndex, { search: e.target.value })}
                        />
                      </label>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-ghost site-labels-visual-remove"
                      aria-label="Remove link"
                      onClick={() => {
                        update(
                          groups.map((g, gi) =>
                            gi === gIndex
                              ? { ...g, items: g.items.filter((_, ii) => ii !== iIndex) }
                              : g
                          )
                        );
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="footer-category-linked-treks">
                    <div className="footer-category-linked-treks-head">
                      <span className="site-labels-visual-label">Linked treks (click target)</span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={treksLoading || !trekOptions.length}
                        onClick={() =>
                          setRowTrekPickerOpen((prev) => ({ ...prev, [rk]: !pickerOpen }))
                        }
                      >
                        {pickerOpen ? 'Done selecting' : 'Select treks…'}
                      </button>
                    </div>
                    {selectedTreks.length > 0 ? (
                      <div className="footer-category-trek-chips">
                        {selectedTreks.map((t) => (
                          <span className="footer-category-trek-chip" key={t.id}>
                            {t.name}
                            <button
                              type="button"
                              className="footer-category-trek-chip-remove"
                              aria-label={`Remove ${t.name}`}
                              onClick={() => removeRowTrek(gIndex, iIndex, item, t.id)}
                            >
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="form-hint">No treks linked — uses search text above.</p>
                    )}
                    {pickerOpen ? (
                      <div className="footer-categories-trek-panel card">
                        <input
                          type="search"
                          className="input"
                          placeholder="Search treks…"
                          value={rowTrekSearch[rk] || ''}
                          onChange={(e) =>
                            setRowTrekSearch((prev) => ({ ...prev, [rk]: e.target.value }))
                          }
                        />
                        {treksLoading ? (
                          <p className="form-hint">Loading treks…</p>
                        ) : (
                          <div
                            className="blog-home-checkbox-list footer-categories-trek-checkboxes"
                            role="group"
                            aria-label="Select treks for this link"
                          >
                            {filteredTreks(rowTrekSearch[rk]).map((trek) => {
                              const checked = selectedIds.includes(trek.id);
                              return (
                                <label key={trek.id} className="blog-home-checkbox-item">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleRowTrek(gIndex, iIndex, item, trek.id)}
                                  />
                                  <span className="blog-home-checkbox-title">{trek.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="footer-category-row-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm site-labels-visual-add"
              onClick={() => addLinkRow(gIndex, false)}
            >
              <Plus size={14} /> Add link row
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-secondary site-labels-visual-add"
        onClick={() => update([...groups, { heading: '', items: [{ label: '', search: '' }] }])}
      >
        <Plus size={16} /> Add column
      </button>
    </div>
  );
}
